import type { ScratchcardTeaserPayload } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { KnownProductType } from '#api/shared/@types/generated/backend/graphql-schema-types'
import { QUERY_PRICES } from '~/constants/queryParams'
import { useFetchOptions } from '~/custom-pages/home/utils/fetchOptions'

const ascending = (a: number, b: number) => a - b

const isNumber = (val: number) => !isNaN(val)

const toPrice = (acc: number[], teaser: ScratchcardTeaserPayload) => {
  const newPrice = teaser.price?.value
  if (!newPrice || acc.includes(newPrice)) return acc

  acc.push(newPrice)
  return acc
}

export const useScratchcardFilter = (options: { eager?: boolean } = {}) => {
  const router = useRouter()
  const licensedTerritory = useLicensedTerritory({ raw: true })

  const filters = useState<number[]>(() => [])

  const isScratchRoute = computed(() => router.currentRoute.value.path.includes('/scratch'))

  const enabled = options.eager ? true : isScratchRoute

  const { loading, result } = useProductCatalogQuery(
    { licensedTerritory, productType: KnownProductType.Scratchcard },
    {
      enabled,
      ...useFetchOptions(),
    },
  )

  const teasers = computed<ScratchcardTeaserPayload[]>(() => {
    const productCatalog = result.value?.productCatalog
    if (!productCatalog || !('elements' in productCatalog)) return []

    return (productCatalog.elements ?? []) as ScratchcardTeaserPayload[]
  })

  const prices = computed(() => teasers.value.reduce(toPrice, []).sort(ascending))

  const activePrices = computed(() =>
    filters.value.filter((filter) => prices.value.includes(filter)).sort(ascending),
  )

  const filteredTeasers = computed(() => {
    if (activePrices.value.length <= 0) return teasers.value

    return teasers.value.filter(
      (teaser) => !!teaser.price && activePrices.value.includes(teaser.price.value),
    )
  })

  const handleSetFilter = async (newPriceFilter: number) => {
    setFilter(newPriceFilter)
    await updateQueryParams(activePrices.value)
  }

  const setFilter = (price: number) => {
    const filterSet = new Set(filters.value)

    if (filterSet.has(price)) {
      filterSet.delete(price)
    } else {
      filterSet.add(price)
    }

    filters.value = Array.from(filterSet)
  }

  const updateQueryParams = async (prices: number[]) => {
    if (!isScratchRoute.value) return

    await router.replace({
      query: {
        ...router.currentRoute.value.query,
        [QUERY_PRICES]: prices.length >= 1 ? prices : undefined,
      },
    })
  }

  onServerPrefetch(() => {
    const queryFilters = router.currentRoute.value.query[QUERY_PRICES]
    if (!queryFilters) return

    const queryArray = Array.isArray(queryFilters) ? queryFilters : [queryFilters]
    filters.value = queryArray.map(Number).filter(isNumber)
  })

  return {
    activePrices: readonly(activePrices),
    filteredTeasers: readonly(filteredTeasers),
    loading,
    prices: readonly(prices),
    setFilter: handleSetFilter,
  }
}
